import React, { useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { BlockMath } from "./KaTeX";
import { imageBlockPath } from "./QuestionContent";
import RichTextEditor from "./RichTextEditor";
import { ArrowUp, ArrowDown, Trash2, Type, Sigma, Image as ImageIcon, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function uploadImageFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads/image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.path;
}

export default function BlockEditor({ blocks, onChange }) {
  const inputRef = useRef(null);

  const setBlock = (i, next) => {
    const copy = [...blocks];
    copy[i] = next;
    onChange(copy);
  };
  const removeBlock = (i) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const addBlock = async (type) => {
    if (type === "image") {
      inputRef.current?.click();
      return;
    }
    onChange([...blocks, { type, value: "" }]);
  };
  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const path = await uploadImageFile(f);
      onChange([...blocks, { type: "image", value: "", image_path: path }]);
      toast.success("Görsel eklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Görsel yüklenemedi");
    }
  };

  return (
    <div className="space-y-3">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
      <div className="flex flex-wrap gap-2">
        <AddBtn onClick={() => addBlock("text")} icon={<Type size={14} />} label="Metin bloğu ekle" testId="block-add-text" />
        <AddBtn onClick={() => addBlock("latex")} icon={<Sigma size={14} />} label="Matematik (LaTeX) bloğu ekle" testId="block-add-latex" />
        <AddBtn onClick={() => addBlock("image")} icon={<ImageIcon size={14} />} label="Görsel bloğu ekle" testId="block-add-image" />
      </div>

      {blocks.length === 0 && (
        <div className="text-sm text-slate-500 border border-dashed border-[#262626] rounded-lg p-6 text-center">
          Henüz blok yok. Yukarıdan Metin, LaTeX veya Görsel bloğu ekleyin.
        </div>
      )}

      <ol className="space-y-2">
        {blocks.map((b, i) => (
          <li key={i} data-testid={`block-item-${i}`}
              className="rounded-lg border border-[#262626] bg-[#0F0F0F]">
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#262626] bg-[#171717]">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-slate-400">
                <span className="text-[#EA580C]">#{i + 1}</span>
                <BlockTypeBadge type={b.type} />
              </div>
              <div className="flex items-center gap-1">
                <IconBtn onClick={() => move(i, -1)} disabled={i === 0} title="Yukarı" testId={`block-up-${i}`}><ArrowUp size={14} /></IconBtn>
                <IconBtn onClick={() => move(i, 1)} disabled={i === blocks.length - 1} title="Aşağı" testId={`block-down-${i}`}><ArrowDown size={14} /></IconBtn>
                <IconBtn onClick={() => removeBlock(i)} title="Sil" danger testId={`block-remove-${i}`}><Trash2 size={14} /></IconBtn>
              </div>
            </div>
            <div className="p-3">
              {b.type === "text" && (
                <RichTextEditor
                  value={b.value || ""}
                  onChange={(html) => setBlock(i, { ...b, value: html })}
                  testId={`block-text-${i}`}
                  placeholder="Düzyazı… (kalın/italik/altı çizili için üstteki araç çubuğunu kullanın)"
                />
              )}
              {b.type === "latex" && (
                <div className="grid md:grid-cols-2 gap-2">
                  <textarea data-testid={`block-latex-${i}`} rows={3} value={b.value || ""}
                    onChange={(e) => setBlock(i, { ...b, value: e.target.value })}
                    placeholder="LaTeX kaynağı, örn: \\frac{a}{b}, \\sqrt{x}, |x-3|=5"
                    className="w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#262626] focus:border-[#EA580C] outline-none font-mono text-sm" />
                  <div className="rounded-md bg-[#0A0A0A] border border-[#262626] p-3 min-h-[80px] overflow-x-auto">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Önizleme</div>
                    {b.value ? <BlockMath>{b.value}</BlockMath> : <span className="text-xs text-slate-500">…</span>}
                  </div>
                </div>
              )}
              {b.type === "image" && (
                <div className="flex items-center gap-3">
                  {imageBlockPath(b) ? (
                    <img alt="blok gorseli"
                         src={`${BACKEND_URL}/api/uploads/file?path=${encodeURIComponent(imageBlockPath(b))}`}
                         className="max-h-40 rounded-md border border-[#262626]" />
                  ) : (
                    <div className="w-32 h-24 grid place-items-center bg-[#0A0A0A] border border-dashed border-[#262626] text-xs text-slate-500">yok</div>
                  )}
                  <div className="flex flex-col gap-2">
                    <ReplaceImageBtn onPath={(p) => setBlock(i, { ...b, image_path: p })} label={imageBlockPath(b) ? "Görseli değiştir" : "Görsel yükle"} testId={`block-image-upload-${i}`} />
                    {b.value && !imageBlockPath({ value: b.value }) && (
                      <div className="text-[11px] text-slate-400 max-w-[320px]">Açıklama: {b.value}</div>
                    )}
                    <div className="text-[11px] font-mono text-slate-500 max-w-[240px] truncate">{imageBlockPath(b) || "—"}</div>
                  </div>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function AddBtn({ onClick, icon, label, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#262626] bg-[#171717] hover:border-[#EA580C] text-sm">
      {icon}<Plus size={12} className="opacity-60" />{label}
    </button>
  );
}
function IconBtn({ children, onClick, disabled, title, danger, testId }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} data-testid={testId}
      className={`p-1.5 rounded-md border border-transparent hover:bg-[#0A0A0A] disabled:opacity-30 ${danger ? "text-rose-400" : "text-slate-300"}`}>
      {children}
    </button>
  );
}
function BlockTypeBadge({ type }) {
  const map = {
    text: { label: "Metin", cls: "text-slate-300" },
    latex: { label: "LaTeX", cls: "text-[#FDBA74]" },
    image: { label: "Görsel", cls: "text-emerald-300" },
  };
  const m = map[type] || map.text;
  return <span className={`${m.cls}`}>{m.label}</span>;
}

export function ReplaceImageBtn({ onPath, label = "Görsel yükle", testId }) {
  const ref = useRef(null);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const path = await uploadImageFile(f);
      onPath(path);
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Görsel yüklenemedi");
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} />
      <button type="button" onClick={() => ref.current?.click()} data-testid={testId}
        className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#262626] hover:border-[#EA580C]">
        <ImageIcon size={12} /> {label}
      </button>
    </>
  );
}
